import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";

export const useFileUpload = (
  setErrorMessage: (message: string) => void,
  setShowError: (show: boolean) => void
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log("No file selected");
      return;
    }

    setIsProcessing(true);

    try {
      if (!file.type.startsWith("video/")) {
        throw new Error("Selected file is not a video.");
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          if (!e.target || !e.target.result) {
            throw new Error("Failed to read file.");
          }

          const arrayBuffer = e.target.result as ArrayBuffer;
          const blob = new Blob([arrayBuffer], { type: file.type });
          navigate("/apply/review", { state: { blob: blob } });
        } catch (error) {
          console.error("Error processing file data:", error);
          const errorMsg =
            error instanceof Error
              ? error.message
              : "Error processing video data.";
          setErrorMessage(errorMsg);
          setShowError(true);
        } finally {
          setIsProcessing(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      };

      reader.onerror = () => {
        console.error("FileReader error");
        setErrorMessage(
          "Error reading the video file. Please try again with a different file."
        );
        setShowError(true);
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error processing video file:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Error processing video file.";
      setErrorMessage(errorMsg);
      setShowError(true);
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRequestGallery = () => {
    ReactGA.event({
      category: "Upload Video",
      action: "Upload Video From Gallery Clicked",
      label: "Upload Video From Gallery Clicked"
    });
    if (
      !window.File ||
      !window.FileReader ||
      !window.FileList ||
      !window.Blob
    ) {
      console.error("The File APIs are not fully supported in this browser.");
      setErrorMessage(
        "Your browser does not support video uploads. Please try a different browser."
      );
      setShowError(true);
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("File input ref is not available");
      setErrorMessage("Could not access your gallery. Please try again.");
      setShowError(true);
    }
  };

  return {
    isProcessing,
    fileInputRef,
    handleFileChange,
    handleRequestGallery,
  };
};
